import { GenerateSlidesModal } from "@/components/lessonGenerator/GenerateSlidesModal";
import {
  type DraftJob,
  StatusPill,
  audioProgress,
  isActionable,
  whenItRan,
} from "@/components/lessonGenerator/draftStatus";
import { NotFound } from "@/components/shared/NotFound";
import { MainSidebar } from "@/components/sidebars/MainSidebar";
import { BreadcrumbSkeleton } from "@/components/skeletons/BreadcrumbSkeleton";
import { GetProjectBreadcrumbsDocument } from "@/generated/graphql";
import { useProjectGenerationJobs } from "@/lib/hooks/lessonGenerator/useLessonGeneration";
import { formatCostPerSlide, formatTwd } from "@/lib/utils/aiCost";
import { describeAiError } from "@/lib/utils/aiCredits";
import { hasGraphQLErrorCode } from "@/lib/utils/graphqlErrors";
import { ErrorCodes } from "@ai-teach/types";
import { Breadcrumb, Button, LoadingSpinner } from "@ai-teach/ui-web";
import { useQuery } from "@apollo/client/react";
import { useParams } from "@tanstack/react-router";
import { useState } from "react";

interface ProjectDraftJob extends DraftJob {
  lessonId: string;
  lessonName?: string | null;
  batchId?: string | null;
}

interface Batch {
  key: string;
  startedAt: unknown;
  jobs: ProjectDraftJob[];
}

function intoBatches(jobs: ProjectDraftJob[]): Batch[] {
  const batches: Batch[] = [];

  for (const job of jobs) {
    const key = job.batchId ?? job.id;
    const existing = batches.find((one) => one.key === key);
    if (existing) existing.jobs.push(job);
    else batches.push({ key, startedAt: job.createdAt, jobs: [job] });
  }

  return batches;
}

function batchSpend(batch: Batch): { calls: number; costUsd: number } {
  return batch.jobs.reduce(
    (sum, job) => ({
      calls: sum.calls + (job.usage?.calls ?? 0),
      costUsd: sum.costUsd + (job.usage?.costUsd ?? 0),
    }),
    { calls: 0, costUsd: 0 }
  );
}

function describeBatch(batch: Batch): string {
  const ready = batch.jobs.filter(
    (job) => job.status === "done" && job.writtenSlideIds.length === 0
  ).length;
  const running = batch.jobs.filter(
    (job) => job.status === "pending" || job.status === "running"
  ).length;
  const failed = batch.jobs.filter((job) => job.status === "failed").length;

  return [
    running > 0 ? `${running} still generating` : null,
    ready > 0 ? `${ready} ready to review` : null,
    failed > 0 ? `${failed} failed` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

function DraftRow({
  job,
  onReview,
  onDiscard,
}: {
  job: ProjectDraftJob;
  onReview: () => void;
  onDiscard: () => void;
}) {
  const written = job.writtenSlideIds.length > 0;
  const isRunning = job.status === "pending" || job.status === "running";
  const audio = audioProgress(job);

  return (
    <li className="flex items-center gap-4 rounded-lg border border-gray-200 px-4 py-3 dark:border-gray-800">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill job={job} />
          <span className="truncate text-sm font-semibold text-secondary-200">
            {job.lessonName ?? "Lesson"}
          </span>
          <span className="text-sm text-gray-500">
            {written
              ? `${job.writtenSlideIds.length} slides added`
              : `${job.draft.length || job.progress.total} slides`}
          </span>
          {job.usage &&
            (job.usage.calls > 0 ? (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 font-mono text-[11px] text-gray-600 dark:bg-white/5 dark:text-gray-300">
                {formatTwd(job.usage.costUsd)}
              </span>
            ) : (
              <span className="text-[11px] text-gray-400">cost not recorded</span>
            ))}
        </div>

        {job.usage && job.usage.calls > 0 && (
          <p className="mt-1 text-xs text-gray-500">
            {job.usage.calls} model call{job.usage.calls === 1 ? "" : "s"} ·{" "}
            {formatCostPerSlide(job.usage.costUsd, job.usage.slidesUsable)}
            {job.usage.wastedCostUsd > 0 &&
              ` · ${formatTwd(job.usage.wastedCostUsd)} redrafted away`}
          </p>
        )}
        {isRunning && (
          <p className="mt-1 text-xs text-gray-500">
            {job.progress.done} of {job.progress.total} drafted
          </p>
        )}
        {audio && (
          <p className="mt-1 text-xs text-gray-500">
            {audio.done} of {audio.total} slides have their audio
          </p>
        )}
        {job.error && <p className="mt-1 text-xs text-error-500">{describeAiError(job.error)}</p>}
      </div>

      <div className="flex shrink-0 gap-2">
        {job.status === "done" && !written && (
          <Button size="sm" onClick={onReview}>
            Review
          </Button>
        )}
        {!written && (
          <Button variant="secondary" size="sm" onClick={onDiscard}>
            Discard
          </Button>
        )}
      </div>
    </li>
  );
}

export function ProjectDraftsPage() {
  const { projectId } = useParams({ from: "/admin/project/$projectId/drafts" });
  const [reviewing, setReviewing] = useState<ProjectDraftJob | null>(null);

  const {
    data,
    loading: breadcrumbsLoading,
    error,
  } = useQuery(GetProjectBreadcrumbsDocument, {
    variables: { id: projectId },
    fetchPolicy: "cache-first",
  });

  const { jobs, loading, refetch, discard } = useProjectGenerationJobs(projectId);

  if (hasGraphQLErrorCode(error, ErrorCodes.NOT_FOUND)) {
    return <NotFound title="Project not found" description="This project doesn't exist." />;
  }

  const breadcrumbs = data?.project?.breadcrumbs;
  const batches = intoBatches(jobs.filter(isActionable));

  return (
    <div className="flex h-screen">
      <MainSidebar />
      <div className="flex-1 overflow-auto">
        <div className="mx-auto max-w-4xl p-6">
          {breadcrumbsLoading ? (
            <BreadcrumbSkeleton className="mb-5" />
          ) : (
            <Breadcrumb
              items={[
                { label: "Home", to: "/admin" },
                ...(breadcrumbs?.map((one) => ({
                  label: one.name,
                  to: `/admin/project/${one.id}`,
                })) ?? []),
                { label: "Drafts" },
              ]}
              className="mb-4"
            />
          )}

          <h1 className="my-5 text-2xl font-bold">Generated lessons</h1>

          {loading && jobs.length === 0 ? (
            <div className="flex justify-center py-16">
              <LoadingSpinner />
            </div>
          ) : batches.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 px-4 py-12 text-center dark:border-gray-700">
              <p className="body-sm text-gray-500 dark:text-gray-400">
                No drafts waiting in this project.
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {batches.map((batch) => (
                <section key={batch.key}>
                  <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
                    <h2 className="card-title">
                      {batch.jobs.length} lesson{batch.jobs.length === 1 ? "" : "s"}
                      {batchSpend(batch).calls > 0 && (
                        <span className="ml-2 font-mono text-sm font-normal text-gray-500">
                          {formatTwd(batchSpend(batch).costUsd)}
                        </span>
                      )}
                    </h2>
                    <span className="text-xs text-gray-500">
                      {whenItRan(batch.startedAt)}
                      {describeBatch(batch) ? ` · ${describeBatch(batch)}` : ""}
                    </span>
                  </div>
                  <ul className="space-y-2">
                    {batch.jobs.map((job) => (
                      <DraftRow
                        key={job.id}
                        job={job}
                        onReview={() => setReviewing(job)}
                        onDiscard={() => discard(job.id)}
                      />
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </div>
      </div>

      {reviewing && !breadcrumbsLoading && (
        <GenerateSlidesModal
          isOpen={true}
          onClose={() => setReviewing(null)}
          lessonId={reviewing.lessonId}
          resumeJobId={reviewing.id}
          onSlidesAdded={refetch}
        />
      )}
    </div>
  );
}
